import {
  differenceInMinutes,
  parse,
  parseISO,
  isEqual,
  isBefore,
  isAfter,
} from 'date-fns';
import { firestore } from './firebase';
import XLSX from 'xlsx';

const convertToDate = (date, time) => {
  const dateString = `${date} ${time}`;
  return parse(dateString, 'yyyy-MM-dd HH:mm', new Date());
};

export const createEntry = (userId, entry) => {
  const minutesLogged = differenceInMinutes(
    convertToDate(entry.date, entry.end),
    convertToDate(entry.date, entry.start)
  );

  return firestore
    .collection('users')
    .doc(userId)
    .collection('log')
    .add({ ...entry, minutes: minutesLogged });
};

export const deleteEntries = (userId, entryIds) => {
  return Promise.all(
    entryIds.map((id) =>
      firestore
        .collection('users')
        .doc(userId)
        .collection('log')
        .doc(id)
        .delete()
    )
  );
};

const getRowsByVolunteer = (allRows) => {
  const reducedRows = allRows.slice(1).reduce((acc, row) => {
    const volunteer = `${row[0]} ${row[1]}`;
    return {
      ...acc,
      [volunteer]: {
        entries: acc[volunteer] ? Number(acc[volunteer].entries) + 1 : 1,
        hours: acc[volunteer]
          ? acc[volunteer].hours + Number(row[7])
          : Number(row[7]),
      },
    };
  }, {});

  return Object.keys(reducedRows).reduce(
    (acc, volunteer) => {
      return [
        ...acc,
        [
          volunteer,
          reducedRows[volunteer].entries,
          reducedRows[volunteer].hours,
        ],
      ];
    },
    [['Volunteer', '# of Entries', 'Total Hours']]
  );
};

const getRowsByEvent = (allRows) => {
  const reducedRows = allRows.slice(1).reduce((acc, row) => {
    const event = row[4];
    return {
      ...acc,
      [event]: {
        entries: acc[event] ? Number(acc[event].entries) + 1 : 1,
        hours: acc[event] ? acc[event].hours + Number(row[7]) : Number(row[7]),
      },
    };
  }, {});

  return Object.keys(reducedRows).reduce(
    (acc, event) => {
      return [
        ...acc,
        [event, reducedRows[event].entries, reducedRows[event].hours],
      ];
    },
    [['Event', '# of Entries', 'Total Hours']]
  );
};

const filterRows = (allRows, start, end) => {
  const startDate = parseISO(start);
  const endDate = parseISO(end);

  return allRows.filter((row) => {
    const date = parse(row[3], 'M/d/yy', new Date());

    const isEqualOrAfter = isEqual(startDate, date) || isAfter(date, startDate);
    const isEqualOrBefore = isEqual(endDate, date) || isBefore(date, endDate);

    return isEqualOrAfter && isEqualOrBefore;
  });
};

export const generateReport = (
  rows,
  { reportStart, reportEnd },
  closeDialog
) => {
  const reportRows =
    reportStart && reportEnd ? filterRows(rows, reportStart, reportEnd) : rows;

  if (!reportRows.length) {
    alert('No entries were found for that range.');
    return;
  }

  closeDialog();

  const reportWorkbook = XLSX.utils.book_new();
  const allEntriesSheet = XLSX.utils.aoa_to_sheet(reportRows);

  const volunteerSummaryRows = getRowsByVolunteer(reportRows);
  const volunteerSummarySheet = XLSX.utils.aoa_to_sheet(volunteerSummaryRows);

  const eventSummaryRows = getRowsByEvent(reportRows);
  const eventSummarySheet = XLSX.utils.aoa_to_sheet(eventSummaryRows);

  XLSX.utils.book_append_sheet(
    reportWorkbook,
    volunteerSummarySheet,
    'Summary by Volunteer'
  );

  XLSX.utils.book_append_sheet(
    reportWorkbook,
    eventSummarySheet,
    'Summary by Event'
  );

  XLSX.utils.book_append_sheet(reportWorkbook, allEntriesSheet, 'All Entries');

  const range =
    reportStart &&
    reportEnd &&
    `(${reportStart.replace('/', '.')} to ${reportEnd.replace('/', '.')})`;

  const suffix = reportStart && reportEnd ? range : '- All Entries';

  XLSX.writeFile(reportWorkbook, `TSNW Volunteer Hours Report ${suffix}.xlsx`);
};
